import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Skeleton,
  Switch,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useAutoAcceptSettings } from 'modules/Settings/hooks/useAutoAcceptSettings';

const useStyles = makeStyles((theme) => ({
  content: {
    paddingBottom: theme.spacing(4)
  },
  actions: {
    paddingBottom: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  skeleton: {
    minWidth: theme.spacing(45)
  }
}));

interface AutoAcceptSettingsProps {
  onClose: () => void;
  onSave: ({ autoAcceptEnabled }: { autoAcceptEnabled: boolean }) => void;
}

const AutoAcceptSettings: React.FC<AutoAcceptSettingsProps> = ({
  onClose,
  onSave
}: AutoAcceptSettingsProps) => {
  const classes = useStyles();
  const { data, loading } = useAutoAcceptSettings();
  const initilaValue = useMemo(
    () => data?.getAutoAcceptSettings?.enabled ?? true,
    [data?.getAutoAcceptSettings?.enabled]
  ); // for first time user, auto accept settings is enabled

  const [autoAccept, setAutoAccept] = useState(initilaValue);

  const handleToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAutoAccept(event.target.checked);
    },
    []
  );

  const handleSave = useCallback(() => {
    onSave({ autoAcceptEnabled: autoAccept });
  }, [onSave, autoAccept]);

  useEffect(() => {
    setAutoAccept(data?.getAutoAcceptSettings?.enabled ?? true);
  }, [data?.getAutoAcceptSettings?.enabled]);

  const isDisabled = useMemo(() => loading || initilaValue === autoAccept, [
    autoAccept,
    initilaValue,
    loading
  ]);

  return (
    <>
      <DialogContent className={classes.content}>
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <Grid container>
            <Grid item xs={3}>
              <Switch
                checked={autoAccept}
                data-testid="settings-auto-select-toggle"
                onChange={handleToggle}
              />
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body1" gutterBottom>
                <FormattedMessage id="settingsDialog.autoSelect" />
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <FormattedMessage id="settingsDialog.description" />
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          color="primary"
          variant="text"
          onClick={onClose}
          data-testid="settings-auto-select-cancel-button"
          disabled={loading}
        >
          <FormattedMessage id="settingsDialog.cancel" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          data-testid="settings-auto-select-save-button"
          disabled={isDisabled}
        >
          <FormattedMessage id="settingsDialog.save" />
        </Button>
      </DialogActions>
    </>
  );
};

const LoadingSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <Grid container data-testid="settings-loading-screen">
      <Grid item xs={12}>
        <Skeleton className={classes.skeleton} />
        <Skeleton className={classes.skeleton} />
      </Grid>
    </Grid>
  );
};

export default React.memo(AutoAcceptSettings);
